import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search } from "lucide-react";
import ParticlesBackground from "@/components/ParticlesBackground";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function UserHistoryPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const searchHistory = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Введите email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/user-history/${email}`);
      setHistory(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info("Заявок не найдено");
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Ошибка загрузки истории");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "text-yellow-500";
      case "awaiting_confirmation":
        return "text-secondary";
      case "completed":
        return "text-green-500";
      case "cancelled":
        return "text-red-500";
      default:
        return "text-muted-foreground";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Ожидание";
      case "awaiting_confirmation":
        return "Проверка оплаты";
      case "completed":
        return "Завершено";
      case "cancelled":
        return "Отменено";
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <ParticlesBackground />
      
      {/* Header */}
      <header className="border-b border-border relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center gap-4">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            size="icon"
            className="hover:bg-primary/10 hover:text-primary"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </Button>
          <h1 className="font-heading text-2xl font-bold text-primary tracking-tight">
            ИСТОРИЯ ОБМЕНОВ
          </h1>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 py-20 relative z-10">
        {/* Search Form */}
        <div className="glassmorphism p-8 mb-8 animate-in">
          <h2 className="font-heading text-xl font-bold mb-6 text-primary uppercase tracking-wide">
            НАЙТИ МОИ ЗАЯВКИ
          </h2>
          <form onSubmit={searchHistory} className="flex gap-4">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 h-12 bg-input border-transparent focus:border-primary"
              data-testid="email-search-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-12 bg-primary text-primary-foreground hover:translate-x-1 hover:translate-y-1 transition-transform font-bold uppercase brutal-shadow"
              data-testid="search-button"
            >
              <Search size={16} className="mr-2" />
              {loading ? "ПОИСК..." : "НАЙТИ"}
            </Button>
          </form>
        </div>

        {/* Results */}
        {searched && (
          <div className="space-y-4">
            {history.length === 0 ? (
              <div className="glassmorphism p-8 text-center text-muted-foreground">
                Заявок не найдено
              </div>
            ) : (
              history.map((request) => (
                <div
                  key={request.id}
                  className="glassmorphism p-6 hover:border-primary/50 transition-colors"
                  data-testid={`history-item-${request.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className={`font-mono font-bold text-sm uppercase ${getStatusColor(request.status)}`}>
                        {getStatusText(request.status)}
                      </span>
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(request.created_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">
                      ID: {request.id.substring(0, 8)}...
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-card border border-border p-4">
                      <div className="text-xs text-muted-foreground mb-1">Отправляете</div>
                      <div className="font-mono font-bold text-lg">
                        {request.from_amount} {request.from_currency.toUpperCase()}
                      </div>
                    </div>
                    <div className="bg-card border border-border p-4">
                      <div className="text-xs text-muted-foreground mb-1">Получаете</div>
                      <div className="font-mono font-bold text-lg text-primary">
                        {request.to_amount} {request.to_currency.toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {request.status === "pending" && (
                    <div className="bg-card border border-border p-3">
                      <div className="text-xs text-muted-foreground mb-1">Адрес для оплаты</div>
                      <div className="font-mono text-xs break-all">{request.deposit_address}</div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserHistoryPage;
